/**
 * Section view panel — a small floating control cluster: axis, offset
 * slider, flip. Purely a view setting (no timeline impact).
 */

import type { App } from '../app';

export function mountSectionPanel(host: HTMLElement, app: App): void {
  const panel = document.createElement('div');
  panel.className = 'nv-section';
  panel.style.display = 'none';
  host.appendChild(panel);

  const render = (): void => {
    const cfg = app.section;
    const open = app.sectionOpen && cfg !== null;
    panel.style.display = open ? 'flex' : 'none';
    if (!open || !cfg) return;
    panel.innerHTML = '';

    const title = document.createElement('span');
    title.className = 'sec-title';
    title.textContent = 'Section';
    panel.appendChild(title);

    for (const axis of ['X', 'Y', 'Z'] as const) {
      const b = document.createElement('button');
      b.className = `sec-axis${cfg.axis === axis ? ' active' : ''}`;
      b.textContent = axis;
      b.addEventListener('click', () => {
        const r = app.viewport.bodiesRange(axis);
        app.applySection({ axis, offset: (r.min + r.max) / 2, flip: cfg.flip });
      });
      panel.appendChild(b);
    }

    const range = app.viewport.bodiesRange(cfg.axis);
    const pad = Math.max(1, (range.max - range.min) * 0.02);
    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = String(range.min - pad);
    slider.max = String(range.max + pad);
    slider.step = String(Math.max(0.1, (range.max - range.min) / 200));
    slider.value = String(cfg.offset);
    // input (not change): live slicing while dragging.
    slider.addEventListener('input', () => {
      app.applySection({ ...app.section!, offset: Number(slider.value) });
    });
    panel.appendChild(slider);

    const offset = document.createElement('input');
    offset.type = 'text';
    offset.className = 'sec-offset';
    offset.value = String(Math.round(cfg.offset * 100) / 100);
    offset.addEventListener('change', () => {
      const v = Number(offset.value);
      if (Number.isFinite(v)) app.applySection({ ...app.section!, offset: v });
    });
    panel.appendChild(offset);

    const flip = document.createElement('button');
    flip.className = `sec-axis${cfg.flip ? ' active' : ''}`;
    flip.textContent = '⇄';
    flip.title = 'Flip which side is kept';
    flip.addEventListener('click', () =>
      app.applySection({ ...app.section!, flip: !app.section!.flip }),
    );
    panel.appendChild(flip);

    const close = document.createElement('button');
    close.className = 'sec-axis';
    close.textContent = '✕';
    close.title = 'Turn the section view off';
    close.addEventListener('click', () => app.toggleSectionPanel());
    panel.appendChild(close);
  };

  app.onUI(render);
  render();
}
